import {createHash} from 'crypto';

import {fromJS as immutableFromJS, Map as ImmutableMap, Set as ImmutableSet} from 'immutable';

import Metadata from './meta';


/**
 * Commands are *named* state altering operations with serializeable value
 * parameters as payload.
 */
export class Command {
  constructor (payload) {
    Command.ensureUnique(this.name, this.constructor);
    this.payload = immutableFromJS(payload);
    if ( !(this.payload instanceof ImmutableMap ) ) {
      throw new Error(`Expected command payload to convert to an immutable map, but got ${payload}`);
    }
    if ('validatedPayload' in this) {
      this.validatedPayload();
    }

  }
  get name() {
    return this.constructor.name;
  }
  static get isFlushable() {
    return true;
  }

  static get isCommand () {
    return true;
  }

  inspect () {
    return this.toString();
  }

  inspectablePayload() {
    return this.payload.toJS()
  }

  toString (colorize) {
    let name = `command.${this.name}`;
    if (colorize) {
      name = colorize(name);
    }
    try {
      return `${name}(${JSON.stringify(this.inspectablePayload())})`;
    } catch (e) {
      if (/circular/.test(String(e))) {
        return `command.${this.name}(<!circular json!>)`;
      } else {
        throw e;
      }
    }
  }

  diffInfo () {
    return this.toString();
  }

  static ensureUnique(commandName, commandClass) {
    if (commandName in this._usedNames) {
      if (this._usedNames[commandName] !== commandClass) {
        throw new Error(`Command class ${commandClass} cannot used command name ${commandName} as it is already used by class ${this._usedNames[commandName]}`);
      }
    } else {
      this._usedNames[commandName] = commandClass;
    }
  }


  toComparableValue () {
    return new ImmutableMap({
      commandName: this.name,
      payload: this.payload
    })
  }

}

Command._usedNames = new WeakMap();

/**
 * `ContentCommand`s are `Command` objects that apply to content objects
 * identified by a path. `ContentCommand`s applied to the same object in the
 * same sequence should reliably end up with the same content at that path.
 * Direct ancestors of `ContentCommand` generally have to do with modifying
 * the state of the content object as a whole rather than its internal data
 * (see also `ContentDataCommand` below).
 *
 * Note that ContentCommands have no notion of which particular state
 * of a content object they originate from. This is done intentionally to allow
 * for unattended reconciliation of state transitions when possible.
 */


export class ContentCommand extends Command {

  validatedPayload () {

    if (!this.contentPath) {
      throw new Error("Content command requires contentPath")
    }
    if (!this.contentClassName) {
      throw new Error("Content command requires contentClassName.")
    }
  }

  get contentPath () {
    return this.payload.get('contentPath');
  }

  get contentClassName () {
    return this.payload.get('contentClassName');
  }

  static get isContentCommand () {
    return true;
  }

  static fromContent (content, additionalPayload={}) {
    return new this({
      contentPath: content.path,
      contentClassName: content.constructor.registeredName,
      ...additionalPayload
    });
  }

  async updatedMetadata (contentData) {
    if (!contentData) {
      throw new Error("no content data given")
    }
    return new Metadata({
      checksum: await this.constructor.calculateChecksum(contentData),
      registeredClassName: this.contentClassName
    })
  }
}

export class StructuredDataContentCommand extends ContentCommand {
  static get isStructuredDataContentCommand () {
    return true;
  }

  static async calculateChecksum (data) {
    // @TODO: this is a pretty expensive operation that is going to get called
    // on every single command submission to a repo. it can probably be avoided
    // by taking advantage of immutability (perhaps hashing changes only?)
    let sha1 = createHash('sha1');
    let dataBlob = JSON.stringify(data.toJS());
    return sha1.update(dataBlob).digest().toString('hex');
  }
}

export class DeleteContent extends StructuredDataContentCommand {
  get isDeleteCommand () {
    return true;
  }

}

export class CreateNewContent extends StructuredDataContentCommand {
  validatedPayload () {
    super.validatedPayload();
    if (!this.contentData) {
      throw new Error("New content command is missing content data.")
    }
  }
  get contentData () {
    return this.payload.get('contentData');
  }

  get shouldOverwrite () {
    return this.payload.get('overwrite');
  }

  async updatedMetadata () {
    return await super.updatedMetadata(this.contentData);
  }
}

/**
 * `ContentDataCommand`s are specifically concerned with altering the
 * internal data of content. They provide the very useful `applyToImmutable()`
 * method which allows them to apply their changes to arbitrary internal data
 * of content objects.
 */
class ContentDataCommand extends StructuredDataContentCommand {
  get valuePath () {
    return this.payload.get('valuePath');
  }

  static get isContentDataCommand () {
    return true;
  }

  applyToImmutable (data) {
    throw new Error("Implement me!")
  }
}


export class ChangeContentValue extends ContentDataCommand {
  get newValue () {
    return this.payload.get('newValue');
  }


  applyToImmutable (data) {
    return data.setIn(this.valuePath, this.newValue);
  }


}

export class RemoveContentValue extends ContentDataCommand {
  applyToImmutable (data) {
    return data.deleteIn(this.valuePath);
  }

}




export class AppendContentValue extends ContentDataCommand {

  get newValue () {
    return this.payload.get('newValue');
  }

  applyToImmutable (data) {
    return data.updateIn(this.valuePath, list => list.push(this.newValue));
  }

}


export class SortContentValues extends ContentDataCommand {

  get sortKeyPath () {
    return this.payload.get('sortKeyPath');
  }

  applyToImmutable (data) {
    return data.updateIn(this.valuePath,
      list => list.sortBy((item) => item.getIn(this.sortKeyPath)));
  }


}

export class BinaryContentCommand extends ContentCommand {

  static get isBinaryContentCommand () {
    return true;
  }

  inspectablePayload () {
    let payload = super.inspectablePayload()
    payload.binaryData = "<binary data>";
    return payload;

  }
}


export class CreateBinaryContent extends BinaryContentCommand {
  validatedPayload () {
    super.validatedPayload();
    if (!this.binaryData) {
      throw new Error("New binary content command is missing binary data.")
    }
  }
  get binaryData () {
    return this.payload.get('binaryData');
  }


  async updatedMetadata () {
    return await super.updatedMetadata(this.binaryData);
  }


  static async calculateChecksum (data) {
    let sha1 = createHash('sha1');
    return sha1.update(data).digest().toString('hex');
  }

  static get isCreateBinaryContentCommand () {
    return true;
  }
}

export class SetBinaryContent extends CreateBinaryContent {
  // just like CreateBinaryContent, but doesn't conflict on overwrite
}
export class DeleteBinaryContent extends BinaryContentCommand {
  get isDeleteCommand () {
    return true;
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-repos/commands.js